import moment from 'moment'

import { SERVICE_ID_TO_API_PLAN } from '../ManagePlan/constants'
import { getSuggestedEngageStylePlanName } from '../ManagePlan/suggestedCustomPlanName'
import { directfulServiceNames } from './PrepayPreferences/EditPaymentPeriodDialog/constants'
import { getAvailableSolutionFeatures } from './hotelSolutionFeatures'

const API_PLAN_TO_SERVICE_ID = Object.fromEntries(
  Object.entries(SERVICE_ID_TO_API_PLAN).map(([serviceId, apiPlan]) => [apiPlan, serviceId])
)

export const ENGAGE_BUNDLE_PLANS = new Set([
  'DirectfulCore',
  'Unlimited',
  'Basic',
  'Messaging',
  'Email',
  'CartAbandonment'
])

/** Per-plan labels when each open subscription is shown as its own Plan & Billing row. */
export const ENGAGE_PLAN_ROW_NAMES = {
  DirectfulCore: 'Engage',
  Unlimited: 'Engage+',
  Basic: 'In-property basic',
  Messaging: 'In-property messaging',
  Email: 'Email',
  CartAbandonment: 'Cart Abandonment'
}

const ENGAGE_ANCHOR_PLAN_ORDER = [
  'DirectfulCore',
  'Unlimited',
  'Basic',
  'Messaging',
  'Email',
  'CartAbandonment'
]

/** Subscriptions shown in Plan & Billing (matches PrepayPreferences + SubscriptionsRow). */
export function getVisibleSubscriptions(subscriptions) {
  return (subscriptions || []).filter(s => {
    if (s.startDate === s.endDate) {
      return false
    }
    if (s.endDate && moment(s.endDate).isBefore(moment())) {
      return false
    }
    return true
  })
}

function getPlanRate(subscription) {
  const rate = subscription?.planRate
  if (rate == null || rate === '') {
    return 0
  }
  return Number(rate) || 0
}

function isSubscriptionCurrentlyActive(subscription) {
  return moment(subscription.startDate).isBefore(moment())
}

function getActiveVisibleSubscriptions(subscriptions) {
  return getVisibleSubscriptions(subscriptions).filter(isSubscriptionCurrentlyActive)
}

export function subscriptionsToEngageServiceIds(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions)
  const serviceIds = new Set()
  for (const sub of visible) {
    if (!ENGAGE_BUNDLE_PLANS.has(sub.plan)) {
      continue
    }
    const serviceId = API_PLAN_TO_SERVICE_ID[sub.plan]
    if (serviceId) {
      serviceIds.add(serviceId)
    }
  }
  if (visible.some(s => s.plan === 'DirectfulCore') && !serviceIds.has('core_sms')) {
    serviceIds.add('core_sms')
  }
  return serviceIds
}

export function getEngageStylePlanNameFromSubscriptions(subscriptions) {
  return getSuggestedEngageStylePlanName(subscriptionsToEngageServiceIds(subscriptions))
}

function trimPackageName(value) {
  if (value == null) {
    return ''
  }
  return String(value).trim()
}

/** Package name from API when set on any active engage-bundle subscription. */
export function getStoredPackageName(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions)
  for (const plan of ENGAGE_ANCHOR_PLAN_ORDER) {
    const sub = visible.find(s => s.plan === plan)
    const name = trimPackageName(sub?.packageName)
    if (name) {
      return name
    }
  }
  return ''
}

function hasActiveUnlimitedPlan(subscriptions) {
  return getActiveVisibleSubscriptions(subscriptions).some(s => s.plan === 'Unlimited')
}

function hasDirectfulCorePlanRate(subscriptions) {
  return getActiveVisibleSubscriptions(subscriptions).some(
    s => s.plan === 'DirectfulCore' && getPlanRate(s) > 0
  )
}

export function getEngageAnchorPlan(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions).filter(s =>
    ENGAGE_BUNDLE_PLANS.has(s.plan)
  )
  if (!visible.length) {
    return null
  }
  if (hasActiveUnlimitedPlan(subscriptions) && !hasDirectfulCorePlanRate(subscriptions)) {
    return 'Unlimited'
  }
  for (const plan of ENGAGE_ANCHOR_PLAN_ORDER) {
    if (visible.some(s => s.plan === plan)) {
      return plan
    }
  }
  return visible[0].plan
}

/** Whether this subscription row should appear in Plan & Billing. */
export function shouldShowSubscriptionRow(subscription, allSubscriptions) {
  if (subscription.plan === 'PremiumSupport') {
    return false
  }

  const bucket = getSubscriptionActivityBucket(subscription)
  const bucketSubs = getVisibleSubscriptions(allSubscriptions).filter(
    s => getSubscriptionActivityBucket(s) === bucket
  )
  const hasEngagePlus = bucketSubs.some(s => s.plan === 'Unlimited')

  // Prefer Engage+ when both Engage and Engage+ exist in the same period.
  if (subscription.plan === 'DirectfulCore' && hasEngagePlus) {
    return false
  }

  return true
}

/** Display name for a Plan & Billing row — always the plan label, not the shared package name. */
export function getSubscriptionRowDisplayName(subscription, _allSubscriptions) {
  const { plan } = subscription
  if (ENGAGE_BUNDLE_PLANS.has(plan)) {
    return ENGAGE_PLAN_ROW_NAMES[plan] || plan
  }
  return directfulServiceNames?.[plan] || plan
}

/**
 * Rate shown on a Plan & Billing row.
 * When Engage+ is shown, include Engage rates from the same active/scheduled period.
 */
export function getSubscriptionRowDisplayRate(subscription, allSubscriptions) {
  if (subscription.plan !== 'Unlimited') {
    return getPlanRate(subscription)
  }

  const bucket = getSubscriptionActivityBucket(subscription)
  return getVisibleSubscriptions(allSubscriptions)
    .filter(s => getSubscriptionActivityBucket(s) === bucket)
    .filter(s => s.plan === 'DirectfulCore' || s.plan === 'Unlimited')
    .reduce((sum, s) => sum + getPlanRate(s), 0)
}

function getSubscriptionActivityBucket(subscription) {
  if (isSubscriptionCurrentlyActive(subscription)) {
    return 'active'
  }
  return `future:${moment(subscription.startDate).format('YYYY-MM-DD')}`
}

const CORE_PLAN_TYPES = ['DirectfulCore', 'Unlimited', 'Basic', 'Messaging']
const IN_PROPERTY_PLAN_TYPES = ['Unlimited', 'Basic']

function activeServicesToServiceIds(activeServices) {
  const serviceIds = new Set()
  for (const service of activeServices || []) {
    const serviceId = API_PLAN_TO_SERVICE_ID[service.plan]
    if (serviceId) {
      serviceIds.add(serviceId)
    }
  }
  if (
    (activeServices || []).some(s => s.plan === 'DirectfulCore') &&
    !serviceIds.has('core_sms')
  ) {
    serviceIds.add('core_sms')
  }
  if (
    (activeServices || []).some(s => IN_PROPERTY_PLAN_TYPES.includes(s.plan)) &&
    !serviceIds.has('inproperty')
  ) {
    serviceIds.add('inproperty')
  }
  return serviceIds
}

function activeSubscriptionsToServiceIds(subscriptions) {
  const serviceIds = new Set()
  for (const sub of getActiveVisibleSubscriptions(subscriptions)) {
    const serviceId = API_PLAN_TO_SERVICE_ID[sub.plan]
    if (serviceId) {
      serviceIds.add(serviceId)
    }
  }
  if (
    getActiveVisibleSubscriptions(subscriptions).some(s => s.plan === 'DirectfulCore') &&
    !serviceIds.has('core_sms')
  ) {
    serviceIds.add('core_sms')
  }
  if (
    getActiveVisibleSubscriptions(subscriptions).some(s =>
      IN_PROPERTY_PLAN_TYPES.includes(s.plan)
    ) &&
    !serviceIds.has('inproperty')
  ) {
    serviceIds.add('inproperty')
  }
  return serviceIds
}

function hasActiveCorePlan(subscriptions, activeServices) {
  if ((activeServices || []).some(s => CORE_PLAN_TYPES.includes(s.plan))) {
    return true
  }
  if (subscriptions?.length) {
    return getActiveVisibleSubscriptions(subscriptions).some(s =>
      CORE_PLAN_TYPES.includes(s.plan)
    )
  }
  return false
}

export function hasActiveEmailDripCampaign(subscriptions = [], activeServices = []) {
  if ((activeServices || []).some(s => s.plan === 'Email')) {
    return true
  }
  if (subscriptions?.length) {
    return getActiveVisibleSubscriptions(subscriptions).some(s => s.plan === 'Email')
  }
  return false
}

export function hasActiveCartAbandonment(
  subscriptions = [],
  activeServices = [],
  isSiteAbandonmentMenuVisible = false
) {
  if (isSiteAbandonmentMenuVisible) {
    return true
  }
  if ((activeServices || []).some(s => s.plan === 'CartAbandonment')) {
    return true
  }
  if (subscriptions?.length) {
    return getActiveVisibleSubscriptions(subscriptions).some(s => s.plan === 'CartAbandonment')
  }
  return false
}

function buildValidatedHeaderServiceIds({
  subscriptions,
  activeServices,
  hasEmailDrip,
  hasCartAbandonment
}) {
  const serviceIds = (activeServices || []).length
    ? activeServicesToServiceIds(activeServices)
    : activeSubscriptionsToServiceIds(subscriptions)

  if (!hasEmailDrip) {
    serviceIds.delete('email_drip')
  }
  if (!hasCartAbandonment) {
    serviceIds.delete('cart_abandonment')
  }

  return serviceIds
}

export function getHeaderPlanDisplayInfo({
  subscriptions = [],
  activeServices = [],
  isSiteAbandonmentMenuVisible = false
} = {}) {
  const hasEmailDrip = hasActiveEmailDripCampaign(subscriptions, activeServices)
  const hasCartAbandonment = hasActiveCartAbandonment(
    subscriptions,
    activeServices,
    isSiteAbandonmentMenuVisible
  )

  if (!hasActiveCorePlan(subscriptions, activeServices)) {
    return {
      shouldShow: false,
      displayName: null,
      tier: null,
      hasEmailDrip,
      hasCartAbandonment
    }
  }

  const serviceIds = buildValidatedHeaderServiceIds({
    subscriptions,
    activeServices,
    hasEmailDrip,
    hasCartAbandonment
  })

  if (!serviceIds.has('core_sms') && !serviceIds.has('inproperty')) {
    return {
      shouldShow: false,
      displayName: null,
      tier: null,
      hasEmailDrip,
      hasCartAbandonment
    }
  }

  return {
    shouldShow: true,
    displayName: getSuggestedEngageStylePlanName(serviceIds),
    tier: serviceIds.has('inproperty') ? 'engagePlus' : 'engage',
    hasEmailDrip,
    hasCartAbandonment
  }
}

export function getValidatedEngagePlanLabel({
  subscriptions = [],
  activeServices = [],
  isSiteAbandonmentMenuVisible = false
} = {}) {
  const planInfo = getHeaderPlanDisplayInfo({
    subscriptions,
    activeServices,
    isSiteAbandonmentMenuVisible
  })

  if (!planInfo.shouldShow) {
    return null
  }

  const solutionFeatures = getAvailableSolutionFeatures({
    tier: planInfo.tier,
    hasEmailDrip: planInfo.hasEmailDrip,
    hasCartAbandonment: planInfo.hasCartAbandonment
  })

  return solutionFeatures.planLabel || null
}
